/* eslint-disable import/no-cycle */
import { createSlice } from '@reduxjs/toolkit';
import { AppThunk, IRootState } from '../../interfaces/redux.interface';
import { IUserCredentials, IAuthState } from '../../interfaces/auth.interface';
import { paths } from '../../routes';
import { Constants } from '../../utils';
import authService from '../../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { confirmSignIn, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';

export const selectIsLoading = (state: IRootState) => state.auth.isLoading;
export const selectUser = (state: IRootState) => state.auth.user;
export const selectError = (state: IRootState) => state.auth.error;
export const selectIsAuthorized = (state: IRootState) =>
  state.auth.isAuthorized;

const savedUser = localStorage.getItem('user');

const initialState: IAuthState = {
  isAuthorized: false,
  isLoading: false,
  user: savedUser ? JSON.parse(savedUser) : null,
  error: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    authSuccess: (state, action) => {
      state.isAuthorized =
    typeof action.payload.isAuthorized === 'boolean'
      ? action.payload.isAuthorized
      : true;
      state.isLoading = false;
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    authRemove: () => ({ ...initialState }),
    authError: (state, action) => {
      state.isAuthorized = false;
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

const { setLoading, authSuccess, authError, authRemove } = authSlice.actions;

const signIn = (
  { username, password }: IUserCredentials,
  navigate: ReturnType<typeof useNavigate>
): AppThunk<Promise<object>> => async (dispatch, getState) => {
  dispatch(setLoading(true));

  try {
    const user = await authService.signIn({ username, password });
    const nextStep = user.nextStep?.signInStep;

    if (
      nextStep === Constants.COGNITO_CHALLENGE.newPasswordRequired ||
      nextStep === Constants.COGNITO_CHALLENGE.confirmUser
    ) {
      dispatch(
        authSuccess({
          username,
          email: username,
          isAuthorized:false
        })
      );
      navigate(paths.newPasswordChallenge);
      return user;
    }

    const cognitoUser = await authService.getCognitoUser();
    if (!cognitoUser) throw new Error('Cognito user is null');

    authService.setCognitoUser(cognitoUser);

    const attributes = await fetchUserAttributes();
    const emailVerified = String(attributes?.email_verified ?? '').toLowerCase() === 'true';

    if (!emailVerified) {
      dispatch(
        authSuccess({
          username: cognitoUser.username,
          ...attributes,
          isAuthorized: true,
          emailVerified: false,
        })
      );
    
      navigate(paths.verifyEmail);
      return user;
    }

    dispatch(
      authSuccess({
        username: cognitoUser.username,
        initials: attributes.name
          ? attributes.name.split(' ').map((i: string) => i.charAt(0)).join('')
          : '',
        ...attributes,
      })
    );
    navigate(paths.calculatorDashboard);
    return user;
  } catch (err: any) {
    const message = err?.message || err?.toString?.();

    if (message?.includes("There is already a signed in user")) {
      localStorage.clear();
      return dispatch(signIn({ username, password }, navigate));
    }

    dispatch(authError(err));
    throw new Error(message);
  }
};



const verifyEmail = (
  code: string
): AppThunk<Promise<unknown>> => async (dispatch) => {
  dispatch(setLoading(true));

  try {
    const user = await authService.getCognitoUser();
    if (!user) throw new Error('No authenticated user found');

    const res = await authService.verifyEmail(code);

    const attributes = await fetchUserAttributes();

    dispatch(
      authSuccess({
        username: user.username,
        initials: attributes.name
          ? attributes.name
              .split(' ')
              .map((i: string) => i.charAt(0))
              .join('')
          : '',
        ...attributes,
        email_verified: 'true',
        isAuthorized: true,
      })
    );

    return res;
  } catch (error: any) {
    return error.message || 'Unknown error';
  } finally {
    dispatch(setLoading(false));
  }
};

const resendCode = (): AppThunk<Promise<void>> => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const user = await authService.getCognitoUser();
    if (!user) throw new Error('No authenticated user found');
    await authService.resendCode();
  } catch (error) {
    dispatch(authError(error));
  } finally {
    dispatch(setLoading(false));
  }
};

const forgotPassword = (username: string): AppThunk<Promise<object>> => (
  dispatch
) => {
  dispatch(setLoading(true));

  return authService
    .forgotPassword(username)
    .then((res) => {
      dispatch(setLoading(false));
      return res;
    })
    .catch((error) => {
      dispatch(authError(error));
      throw new Error(error);
    });
};

const forgotPasswordSubmit = (
  username: string,
  code: string,
  password: string
): AppThunk<Promise<void>> => (dispatch) => {
  dispatch(setLoading(true));

  return authService
    .forgotPasswordSubmit(username, code, password)
    .then(() => {
      dispatch(setLoading(false));
    })
    .catch((error) => {
      dispatch(authError(error));
      throw new Error(error);
    });
};

const signOut = (): AppThunk => (dispatch) => {
  authService.signOut().then(() => dispatch(authRemove()));
};

const currentAuthenticatedUser = (): AppThunk<Promise<object>> => async (
  dispatch
) => {
  dispatch(setLoading(true));
  const attributes = await fetchUserAttributes();

  return getCurrentUser()
    .then((user) => {
      dispatch(
        authSuccess({
          username: user.username,
          initials: attributes.name
            ? attributes.name.split(' ').map((i: string) => i.charAt(0)).join('')
            : '',
          ...attributes,
        })
      );
      return user;
    })
    .catch(() => dispatch(authError(null)));
};

const newPasswordChallenge = (
  email: string,
  password: string,
  navigate: ReturnType<typeof useNavigate>
): AppThunk => async (dispatch) => {
  dispatch(setLoading(true));

  const getStandardAttributes = () => {
    const attributes: Record<string, string> = {};
    if (process.env.REACT_APP_ENVIRONMENT !== 'development') {
      attributes.zoneinfo = 'PasswordChallenge/ZoneInfo';
      attributes.gender = 'Password Challenge Gender';
      attributes.family_name = 'Password Challenge Family Name';
      attributes.phone_number = '+14325551212';
      attributes.address = 'Password Challenge Address';
    }
    attributes.name = 'Password Challenge Name';
    return attributes;
  };

  const requiredStandardAttributes = {
    ...getStandardAttributes(),
  };

  try {
    await confirmSignIn({
      challengeResponse: password,
      // options: {
      //   userAttributes: requiredStandardAttributes,
      // },
    });

    navigate("/");
  } catch (error) {
    console.error('Error in newPasswordChallenge:', error);
  } finally {
    dispatch(setLoading(false));
  }
};

export const authReducer = authSlice.reducer;
export const authActions = {
  signIn,
  signOut,
  currentAuthenticatedUser,
  newPasswordChallenge,
  forgotPassword,
  forgotPasswordSubmit,
  verifyEmail,
  resendCode,
};